import {
  type QueryKey,
  type UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { toastApiError } from '@/shared/notifications/apiErrorToast';

type AppMutationOptions<TData, TVariables> = Omit<
  UseMutationOptions<TData, Error, TVariables>,
  'mutationFn' | 'onError'
> & {
  errorMessage: string;
  invalidates?: QueryKey[] | ((variables: TVariables) => QueryKey[]);
};

export function useAppMutation<TData, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  { errorMessage, invalidates, onSuccess, ...options }: AppMutationOptions<TData, TVariables>
) {
  const queryClient = useQueryClient();
  return useMutation<TData, Error, TVariables>({
    ...options,
    mutationFn,
    onSuccess: (...args) => {
      const keys = typeof invalidates === 'function' ? invalidates(args[1]) : (invalidates ?? []);
      for (const queryKey of keys) queryClient.invalidateQueries({ queryKey });
      return onSuccess?.(...args);
    },
    onError: (error) => toastApiError(error, errorMessage),
  });
}
